import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IPrintableReportHeading } from '@models/report-print-heading';
import {
  ClassOverTimeReport
} from '@shared/branded-reports/branded-class-over-time-report-detail/class-over-time-report-detail.model';
import { BehaviorSubject, Subscription } from 'rxjs';
import { BrandedReportsHelpers } from '../branded-reports-helpers';
import { BarColors, BorderColors } from '../zbchart-report/bar-colors.enum';
import { ZbChartReportTableComponent } from '../zbchart-report/zbchart-report-table.component';
import { ZbChartBar, ZbChartBarValue, ZbChartData } from '../zbchart-report/zbchart-report.model';

@Component({
  selector: 'zbp-branded-class-over-time-report-detail',
  imports: [
    ZbChartReportTableComponent,
  ],
  templateUrl: './branded-class-over-time-report-detail.component.html',
  styleUrl: './branded-class-over-time-report-detail.component.scss'
})
export class BrandedClassOverTimeReportDetailComponent implements OnInit, OnChanges {

  @Input() showProgressReportsHelp: boolean = false;
  @Input({ required: true }) classOverTimeReport: ClassOverTimeReport;
  @Input() isPrinting: boolean;
  @Input() printableReportHeadings: IPrintableReportHeading[];

  zbChart: ZbChartData;

  private reports$ = new BehaviorSubject<ClassOverTimeReport>(null);

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private sanitizer: DomSanitizer,
  ) {
  }

  ngOnInit() {
    this.subscriptions.add(
      this.reports$.subscribe((report) => {
        this.createZbChart(report);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.classOverTimeReport) {
      this.reports$.next(changes.classOverTimeReport.currentValue);
    }

    /** When this input changes, the print function is executed */
    if (changes.isPrinting) {
      if (this.isPrinting) {
        this.print();
      }
    }
  }

  createZbChart(
    report: ClassOverTimeReport,
  ): ZbChartData {
    if (!report) {
      return null;
    }

    const chartData = { bars: [] } as ZbChartData;
    chartData.showZeroCount = false;
    const legend = [];

    legend.push({
      label: 'below level',
      color: BarColors.red300,
      id: 'below',
    });
    legend.push({
      label: 'on level',
      color: BarColors.cream300,
      id: 'onLevel',
    });
    legend.push({
      label: 'above level',
      color: BarColors.green300,
      id: 'aboveLevel',
    });
    legend.push({
      label: 'in progress',
      color: BarColors.neutralsWarmGrey400,
      id: 'inProgress',
    });
    legend.push({
      label: 'not assigned',
      color: BarColors.neutralsWarmGrey200,
      id: 'notAssigned',
    });
    legend.push({
      label: 'assigned and not started',
      color: BarColors.white,
      id: 'assignedAndNotStarted',
    });

    chartData.legend = legend;

    report.benchmarkResults.forEach((result) => {
      let ariaLabel = 'Bars, ';
      const row = {} as ZbChartBar;
      const values = [] as ZbChartBarValue[];
      values.push({
        tooltipLabel: 'below level',
        tooltipValue: this.convertZeroToNull(result.belowTotalCount),
        barLabel: result.belowTotalCount,
        percent: result.belowPercentage,
        color: BarColors.red300,
        borderColor: BorderColors.borderForRed300,
        count: this.convertZeroToNull(result.belowTotalCount),
        legendId: 'below',
      });
      values.push({
        tooltipLabel: 'on level',
        tooltipValue: this.convertZeroToNull(result.onLevelTotalCount),
        barLabel: result.onLevelTotalCount,
        percent: result.onLevelPercentage,
        color: BarColors.cream300,
        borderColor: BorderColors.borderForCream300,
        count: this.convertZeroToNull(result.onLevelTotalCount),
        legendId: 'onLevel',
      });
      values.push({
        tooltipLabel: 'above level',
        tooltipValue: this.convertZeroToNull(result.aboveTotalCount),
        barLabel: result.aboveTotalCount,
        percent: result.abovePercentage,
        color: BarColors.green300,
        borderColor: BorderColors.borderForGreen300,
        count: this.convertZeroToNull(result.aboveTotalCount),
        legendId: 'aboveLevel',
      });
      values.push({
        tooltipLabel: 'in progress',
        tooltipValue: this.convertZeroToNull(result.inProgressTotalCount),
        barLabel: result.inProgressTotalCount,
        percent: result.inProgressPercentage,
        color: BarColors.neutralsWarmGrey400,
        borderColor: BorderColors.borderForNeutralsWarmGrey400,
        count: this.convertZeroToNull(result.inProgressTotalCount),
        legendId: 'inProgress',
      });
      values.push({
        tooltipLabel: 'not assigned',
        tooltipValue: this.convertZeroToNull(result.notAssignedTotalCount),
        barLabel: result.notAssignedTotalCount,
        percent: result.notAssignedPercentage,
        color: BarColors.neutralsWarmGrey200,
        borderColor: BorderColors.borderForNeutralsWarmGrey200,
        count: this.convertZeroToNull(result.notAssignedTotalCount),
        legendId: 'notAssigned',
      });
      values.push({
        tooltipLabel: 'assigned and not started',
        tooltipValue: this.convertZeroToNull(result.assignedAndNotStartedTotalCount),
        barLabel: result.assignedAndNotStartedTotalCount,
        percent: result.assignedAndNotStartedPercentage,
        color: BarColors.white,
        borderColor: BorderColors.borderForWhite,
        count: this.convertZeroToNull(result.assignedAndNotStartedTotalCount),
        legendId: 'assignedAndNotStarted',
      });

      values.forEach((v) => {
        if (v.count > 0) {
          ariaLabel += `${v.count} ${v.tooltipLabel}, `;
        }
      });

      row.label = result.assessmentName;
      row.totalCount = result.assignedAndNotStartedTotalCount + result.belowTotalCount
        + result.inProgressTotalCount + result.notAssignedTotalCount
        + result.onLevelTotalCount + result.aboveTotalCount;
      row.values = values;
      row.tooltip = this.sanitizer.bypassSecurityTrustHtml(BrandedReportsHelpers.zbChartTooltipFormatter(row));
      row.ariaLabel = ariaLabel;
      chartData.bars.push(row);
    });

    this.zbChart = chartData;

    return chartData;
  }

  convertZeroToNull(value: number): number | null {
    return value === 0 ? null : value;
  }

  /**
   * Prints report.
   */
  print() {
    // Give time for Printable Header to render
    setTimeout(() => {
      window.print();
    }, 0);
  }
}
